/**
 * AppointmentsCalendar — FASE 2: Vista mes/semana/día (usa toolbar nativo de react-big-calendar)
 */
import React, { useMemo, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";
import "moment/locale/en-gb";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useStyles } from "./styles";
import AppointmentStatusBadge from "../../components/AppointmentStatusBadge";
import { i18n } from "../../translate/i18n";

const localizer = momentLocalizer(moment);

export default function AppointmentsCalendar({ appointments, onSelectEvent }) {
  const classes = useStyles();

  useEffect(() => {
    const lang = (i18n.language || "es").split("-")[0];
    if (lang === "en") moment.locale("en-gb");
    else if (lang === "pt") moment.locale("pt");
    else moment.locale("es");
  }, [i18n.language]);

  const messages = useMemo(
    () => ({
      date: i18n.t("appointmentsModule.calendar.date"),
      time: i18n.t("appointmentsModule.calendar.time"),
      event: i18n.t("appointmentsModule.calendar.event"),
      today: i18n.t("appointmentsModule.calendar.today"),
      previous: i18n.t("appointmentsModule.calendar.previous"),
      next: i18n.t("appointmentsModule.calendar.next"),
      month: i18n.t("appointmentsModule.calendar.month"),
      week: i18n.t("appointmentsModule.calendar.week"),
      day: i18n.t("appointmentsModule.calendar.day"),
      agenda: i18n.t("appointmentsModule.calendar.agenda"),
      noEventsInRange: i18n.t("appointmentsModule.calendar.noEventsInRange"),
    }),
    [i18n.language],
  );

  const events = (appointments || []).map((a) => ({
    id: a.id,
    title: `${a.contactName || i18n.t("appointmentsModule.calendar.noName")} - ${a.serviceName || i18n.t("appointmentsModule.calendar.serviceFallback")}`,
    start: new Date(a.appointmentDate),
    end: moment(a.appointmentDate).add(a.durationMin || 60, "minutes").toDate(),
    resource: a,
  }));

  const getEventDetail = (a) => [
    a.professionalName || null,
    a.confirmationCode ? `${i18n.t("appointmentsModule.calendar.code")}: ${a.confirmationCode}` : null,
    a.durationMin ? `${a.durationMin} min` : null,
    a.modality ? i18n.t(`appointmentsModule.calendar.modality.${a.modality}`, { defaultValue: a.modality }) : null,
    a.locationDetail || null,
  ].filter(Boolean).join(" · ");

  const EventComponent = ({ event }) => (
    <div className={classes.calendarEvent} title={getEventDetail(event.resource)}>
      <strong>{event.title}</strong>
      {getEventDetail(event.resource) && (
        <span style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.2 }}>
          {getEventDetail(event.resource)}
        </span>
      )}
      <AppointmentStatusBadge status={event.resource?.status} />
    </div>
  );

  const sortedAppointments = useMemo(
    () => [...(appointments || [])].sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate)),
    [appointments],
  );

  return (
    <div style={{ marginTop: 16 }}>
      <div className={classes.calendarWrap}>
        <Calendar
          key={i18n.language}
          culture={(i18n.language || "es").split("-")[0]}
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          messages={messages}
          onSelectEvent={onSelectEvent}
          components={{ event: EventComponent }}
          popup
        />
      </div>
      <Paper className={classes.dashboardToday} elevation={0} style={{ marginTop: 16 }}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("appointmentsModule.calendar.detailsListTitle")}
        </Typography>
        {sortedAppointments.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {i18n.t("appointmentsModule.calendar.noEventsInRange")}
          </Typography>
        ) : (
          sortedAppointments.map((a) => (
            <div key={a.id} className={classes.dashboardTodayItem} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <Typography variant="body1">
                  {moment(a.appointmentDate).format("L LT")} - {a.contactName || i18n.t("appointmentsModule.calendar.noName")} - {a.serviceName || i18n.t("appointmentsModule.calendar.serviceFallback")}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  {getEventDetail(a) || i18n.t("appointmentsModule.appointmentModal.dash")}
                </Typography>
                {a.notes && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {i18n.t("appointmentsModule.appointmentModal.notes")}: {a.notes}
                  </Typography>
                )}
                <AppointmentStatusBadge status={a.status} />
              </div>
              <Button size="small" color="primary" variant="outlined" onClick={() => onSelectEvent?.({ resource: a })}>
                {i18n.t("appointmentsModule.calendar.viewDetails")}
              </Button>
            </div>
          ))
        )}
      </Paper>
    </div>
  );
}
